import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader, CollapsibleSection, InfoRow } from "./shared/components";
import {
  getEmployeeProfile,
  getHrEmployeeProfile,
  downloadBiodataPDF,
  downloadHrBiodataPDF,
  updateBiodataAsHR,
  EmployeeProfile as EmployeeProfileType,
  ProfileSection,
} from "./services/eSeparation.service";

const ProfileList: React.FC<{ items: ProfileSection[]; emptyText: string; emptySuccess?: boolean }> = ({
  items,
  emptyText,
  emptySuccess,
}) => {
  if (!items || items.length === 0)
    return (
      <p className={`fst-italic mb-0 ${emptySuccess ? "text-success" : "text-muted"}`}>{emptyText}</p>
    );
  return (
    <>
      {items.map((it, i) => (
        <div key={i} className="border rounded p-2 mb-2" style={{ background: "rgba(0,0,0,0.01)" }}>
          {Object.entries(it.fields).map(([k, v]) => (
            <InfoRow key={k} label={k} value={v} />
          ))}
        </div>
      ))}
    </>
  );
};

/** Editable text field used in HR admin edit mode. */
const EditableField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}> = ({ label, value, onChange, type = "text" }) => (
  <div className="mb-3">
    <label
      className="form-label text-uppercase fw-medium text-muted"
      style={{ fontSize: 11, letterSpacing: 0.4 }}
    >
      {label}
    </label>
    <input
      type={type}
      className="form-control"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const EmployeeProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRole = (location.state as any)?.role as string | undefined;
  const isHRAdmin = locationRole === "HR" || locationRole === "HR Admin";

  const [profile, setProfile] = useState<EmployeeProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);

  // HR admin: look up a specific employee by emp_no
  const [hrEmpNo, setHrEmpNo] = useState("");
  const [hrLookupLoading, setHrLookupLoading] = useState(false);
  // Set once a lookup succeeds — tracks that `profile` now holds ANOTHER
  // employee's record, so download/edit must go through the HR-only routes
  // rather than the self-service ones (which always resolve to the caller).
  const [viewingEmpNo, setViewingEmpNo] = useState<string | null>(null);

  // Editable overrides (HR admin)
  const [editMode, setEditMode] = useState(false);
  const [edits, setEdits] = useState<Partial<EmployeeProfileType>>({});

  const fetchOwnProfile = () => {
    setLoading(true);
    setEditMode(false);
    setEdits({});
    setViewingEmpNo(null);
    getEmployeeProfile().then((res) => {
      setProfile(res.data || null);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchOwnProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHrLookup = async () => {
    const target = hrEmpNo.trim();
    if (!target) return;
    setHrLookupLoading(true);
    setEditMode(false);
    setEdits({});
    const res = await getHrEmployeeProfile(target);
    setProfile(res.data || null);
    setViewingEmpNo(res.data ? target : null);
    setHrLookupLoading(false);
    if (!res.data) toast.error("Employee not found.");
  };

  const handleDownload = async () => {
    setDownloading(true);
    const res = viewingEmpNo
      ? await downloadHrBiodataPDF(viewingEmpNo)
      : await downloadBiodataPDF();
    setDownloading(false);
    if (res.success) toast.success("Biodata PDF generated and downloaded successfully in official DMRC format.");
    else toast.error("Failed to generate PDF.");
  };

  const handleSaveEdits = async () => {
    if (!profile) return;
    setSaving(true);
    const res = await updateBiodataAsHR({ ...profile, ...edits });
    if (res.success) {
      // Re-fetch the canonical record rather than merging optimistically — the
      // backend may normalize values (e.g. father's/mother's name split).
      const refreshed = viewingEmpNo
        ? await getHrEmployeeProfile(viewingEmpNo)
        : await getEmployeeProfile();
      setProfile(refreshed.data || profile);
      setEditMode(false);
      setEdits({});
      toast.success("Biodata updated successfully.");
    } else {
      toast.error(res.message || "Failed to save changes.");
    }
    setSaving(false);
  };

  const edit = (key: keyof EmployeeProfileType, value: string) =>
    setEdits((prev) => ({ ...prev, [key]: value }));

  const val = (key: keyof EmployeeProfileType): string =>
    ((edits[key] as string | undefined) ?? (profile?.[key] as string | undefined) ?? "");

  // Backend already returns a ready-to-render data: URI or http(s) URL.
  const resolvedPhoto = profile?.photo_url || null;

  return (
    <div className="page-wrapper">
      <div className="content pb-0">
        <PageHeader
          title="Employee Profile / Biodata"
          onExit={() => navigate(-1)}
        />

        {/* HR Admin: employee lookup */}
        {isHRAdmin && (
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body">
              <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
                <i className="ti ti-search me-1" />
                Look up Employee Biodata
              </div>
              <div className="d-flex gap-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Employee No."
                  value={hrEmpNo}
                  onChange={(e) => setHrEmpNo(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleHrLookup()}
                />
                <button
                  className="btn btn-outline-primary px-3"
                  onClick={handleHrLookup}
                  disabled={hrLookupLoading || !hrEmpNo.trim()}
                >
                  {hrLookupLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="ti ti-search" />
                  )}
                </button>
                <button
                  className="btn btn-outline-secondary px-3"
                  onClick={() => { setHrEmpNo(""); fetchOwnProfile(); }}
                  title="Clear — show my own biodata"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <div className="text-muted small mt-2">Loading Profile Details...</div>
          </div>
        ) : (
          <>
            {/* Photo + identity header */}
            <div className="card border-0 shadow-sm mb-4 text-center" style={{ borderRadius: 16 }}>
              <div className="card-body">
                {resolvedPhoto ? (
                  <img
                    src={resolvedPhoto}
                    alt={profile?.emp_name || "Employee"}
                    className="mb-2 rounded-circle"
                    style={{ width: 80, height: 80, objectFit: "cover", border: "3px solid #E6F1FB" }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                      const fallback = document.getElementById("profile-photo-fallback");
                      if (fallback) fallback.style.display = "inline-flex";
                    }}
                  />
                ) : null}
                <span
                  id="profile-photo-fallback"
                  className="d-inline-flex align-items-center justify-content-center mb-2"
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 40,
                    background: "#E6F1FB",
                    display: resolvedPhoto ? "none" : "inline-flex",
                  }}
                >
                  <i className="ti ti-user" style={{ fontSize: 36, color: "#185FA5" }} />
                </span>
                <h4 className="mb-0 fw-bold">{profile?.emp_name || "—"}</h4>
                <div className="text-muted">Emp No: {profile?.emp_no || "—"}</div>
                <div className="fw-medium mt-1">{profile?.designation || "—"}</div>
                <div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>
                  {profile?.department || "—"}
                </div>
                {isHRAdmin && profile && (
                  <button
                    className={`btn btn-sm mt-3 ${editMode ? "btn-outline-secondary" : "btn-outline-warning"}`}
                    onClick={() => { setEditMode(!editMode); setEdits({}); }}
                  >
                    <i className={`ti ${editMode ? "ti-x" : "ti-pencil"} me-1`} />
                    {editMode ? "Cancel Editing" : "Edit Biodata"}
                  </button>
                )}
              </div>
            </div>

            <CollapsibleSection title="Personal Particulars" icon="ti-user" iconColor="#185FA5" iconBg="#E6F1FB" defaultExpanded>
              {editMode ? (
                <>
                  <EditableField label="Father's Name" value={val("father_name")} onChange={(v) => edit("father_name", v)} />
                  <EditableField label="Mother's Name" value={val("mother_name")} onChange={(v) => edit("mother_name", v)} />
                  <EditableField label="Date of Birth" value={val("dob")} onChange={(v) => edit("dob", v)} type="date" />
                  <EditableField label="Gender" value={val("gender")} onChange={(v) => edit("gender", v)} />
                  <EditableField label="Marital Status" value={val("marital_status")} onChange={(v) => edit("marital_status", v)} />
                  <EditableField label="Blood Group" value={val("blood_group")} onChange={(v) => edit("blood_group", v)} />
                  <EditableField label="Category" value={val("category")} onChange={(v) => edit("category", v)} />
                  <EditableField label="Nationality" value={val("nationality")} onChange={(v) => edit("nationality", v)} />
                  <EditableField label="PWD Status" value={val("pwd_status")} onChange={(v) => edit("pwd_status", v)} />
                  <EditableField label="Mobile" value={val("mobile")} onChange={(v) => edit("mobile", v)} />
                  <EditableField label="Email" value={val("email")} onChange={(v) => edit("email", v)} type="email" />
                  {/* Religion has no backing column, and addresses are structured
                      multi-column SAP records — both stay read-only even in edit mode. */}
                  <InfoRow label="Religion" value={profile?.religion} />
                  <div className="mt-3 pt-2 border-top">
                    <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Present Address</div>
                    <div className="small mb-2">{profile?.present_address || "—"}</div>
                    <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Permanent Address</div>
                    <div className="small">{profile?.permanent_address || "—"}</div>
                  </div>
                </>
              ) : (
                <>
                  <InfoRow label="Father's Name" value={profile?.father_name} />
                  <InfoRow label="Mother's Name" value={profile?.mother_name} />
                  <InfoRow label="Date of Birth" value={profile?.dob} />
                  <InfoRow label="Gender" value={profile?.gender} />
                  <InfoRow label="Marital Status" value={profile?.marital_status} />
                  <InfoRow label="Blood Group" value={profile?.blood_group} />
                  <InfoRow label="Category" value={profile?.category} />
                  <InfoRow label="Nationality" value={profile?.nationality} />
                  <InfoRow label="Religion" value={profile?.religion} />
                  <InfoRow label="PWD Status" value={profile?.pwd_status} />
                  <InfoRow label="Mobile" value={profile?.mobile} />
                  <InfoRow label="Email" value={profile?.email} />
                  <div className="mt-3 pt-2 border-top">
                    <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Present Address</div>
                    <div className="small mb-2">{profile?.present_address || "—"}</div>
                    <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Permanent Address</div>
                    <div className="small">{profile?.permanent_address || "—"}</div>
                  </div>
                </>
              )}
            </CollapsibleSection>

            <CollapsibleSection title="DMRC Employment Info" icon="ti-building" iconColor="#0F6E56" iconBg="#E1F5EE">
              {editMode ? (
                <>
                  <EditableField label="Status of Appointment" value={val("status_of_appointment")} onChange={(v) => edit("status_of_appointment", v)} />
                  <EditableField label="Employment Type" value={val("emp_type")} onChange={(v) => edit("emp_type", v)} />
                  <EditableField label="Pay Scale" value={val("pay_scale")} onChange={(v) => edit("pay_scale", v)} />
                  <EditableField label="Date of Joining" value={val("doj")} onChange={(v) => edit("doj", v)} type="date" />
                </>
              ) : (
                <>
                  <InfoRow label="Status of Appointment" value={profile?.status_of_appointment} />
                  <InfoRow label="Employment Type" value={profile?.emp_type} />
                  <InfoRow label="Pay Scale" value={profile?.pay_scale} />
                  <InfoRow label="Date of Joining" value={profile?.doj} />
                </>
              )}
            </CollapsibleSection>

            <CollapsibleSection title="Educational Qualifications" icon="ti-school" iconColor="#4A1A6E" iconBg="#F0E6F6">
              <ProfileList items={profile?.qualifications || []} emptyText="No qualifications recorded." />
            </CollapsibleSection>

            <CollapsibleSection title="Experience Before DMRC" icon="ti-briefcase" iconColor="#BA7517" iconBg="#FFF3EC">
              <ProfileList items={profile?.experience_before_dmrc || []} emptyText="No prior experience recorded." />
            </CollapsibleSection>

            {/* Experience in DMRC (Grade-wise) — commented out per requirement
            <CollapsibleSection title="Experience in DMRC (Grade-wise)" icon="ti-trending-up" iconColor="#993C1D" iconBg="#FDE8E0">
              <ProfileList items={profile?.experience_dmrc_grade || []} emptyText="No grade-wise experience records." />
            </CollapsibleSection>
            */}

            {/* Experience in DMRC (Division/Unit) — commented out per requirement
            <CollapsibleSection title="Experience in DMRC (Division/Unit)" icon="ti-git-branch" iconColor="#1D6E5F" iconBg="#E1F5EE">
              <ProfileList items={profile?.experience_dmrc_division || []} emptyText="No division/unit experience records." />
            </CollapsibleSection>
            */}

            <CollapsibleSection title="Trainings Completed" icon="ti-rosette" iconColor="#185FA5" iconBg="#E6F1FB">
              <ProfileList items={profile?.training || []} emptyText="No training records found." />
            </CollapsibleSection>

            {/* <CollapsibleSection title="Major / Minor Penalties" icon="ti-alert-circle" iconColor="#C0282C" iconBg="#FFEBEB">
              <ProfileList items={profile?.penalties || []} emptyText="No active major/minor penalties on record." emptySuccess />
            </CollapsibleSection>

            <CollapsibleSection title="Awards & Recognitions" icon="ti-trophy" iconColor="#BA7517" iconBg="#FFF3EC">
              <ProfileList items={profile?.awards || []} emptyText="No awards on record." />
            </CollapsibleSection> */}
          </>
        )}
      </div>

      {!loading && (
        <div
          className="d-flex justify-content-end gap-2 bg-white border-top py-3 px-2"
          style={{ position: "sticky", bottom: 0, zIndex: 5 }}
        >
          {isHRAdmin && editMode && (
            <button
              className="btn btn-success px-4"
              onClick={handleSaveEdits}
              disabled={saving || Object.keys(edits).length === 0}
            >
              <i className="ti ti-device-floppy me-1" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
          <button className="btn btn-primary px-4" onClick={handleDownload} disabled={downloading}>
            <i className="ti ti-file-text me-1" />
            {downloading ? "Generating PDF..." : "Download Biodata PDF"}
          </button>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfile;
